import { useEffect, useRef, useState } from 'react';
import { IAuthChange } from '../../../../../../models/Account/IAuthChange';
import styles from './AuthenticationModal.module.css';
import { useAppSelector } from '../../../../../../common/hooks';
import { RootState } from '../../../../../../redux/store';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { handleErrorResponse } from '../../../../../../services';
import SHRMMessage from '../../../../../../common/components/SHRMMessage';

type ModalProps = {
  modalShow: boolean;
  modalIsChangeEmail: boolean;
  modalExistingEmail: string;
  modalClose: () => void;
  modalSave: (model: IAuthChange) => Promise<unknown>;
};

const AuthenticationModal = (props: ModalProps) => {
  const token = useAppSelector((state: RootState) => state.ssoUserReducer.token);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid, isDirty },
    watch,
  } = useForm<IAuthChange>({
    mode: 'all',
    defaultValues: {
      id: props.modalExistingEmail,
      oauthToken: token,
      email: props.modalExistingEmail,
      newPassword: '',
      newConfirmation: '',
      existingPassword: '',
    },
    delayError: 100,
  });
  const [popupMesage, setPopupMessage] = useState('');
  const [popupMessageColor, setPopupMessageColor] = useState('');
  const [showPopupMessage, setShowPopupMessage] = useState(false);

  const newPassword = useRef({});
  newPassword.current = watch('newPassword', '');

  const handleSave = (data: any) => {
    // props.modalSave(model);
    props
      .modalSave(data)
      .then((response: any) => {
        props.modalClose();
      })
      .catch((_error: any) => {
        const message = handleErrorResponse(_error);
        setPopupMessage(message);
        setPopupMessageColor('var(--shrmRed)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 6000);
      });
  };

  const changeNewPassword = e => {
    if (e.key === ' ') {
      e.preventDefault();
    }
  };

  const emailValidation = email => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
    if (email.trim() === '') {
      return;
    }
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
  };

  const validatePassword = value => {
    const regexExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
    if (value && value.length >= 10 && regexExp.test(value)) {
      return true;
    } else {
      return 'Password must be at least 10 characters long, include both upper and lower case characters, include one or more numbers.';
    }
  };

  useEffect(() => {
    const values = getValues();
    reset({
      ...values,
      newPassword: '',
      newConfirmation: '',
      existingPassword: '',
    });
  }, [props.modalShow]);
  return (
    <div>
      {props.modalShow && (
        <form className={styles.container} onSubmit={handleSubmit(handleSave)}>
          <div className={styles.content}>
            {props.modalIsChangeEmail ? (
              <div>
                <div className={styles.header}>
                  <label className={styles.title}>Change Email</label>
                  <button className={styles.closeModalButton} onClick={props.modalClose}>
                    ×
                  </button>
                </div>
                <div className={styles.form}>
                  <SHRMMessage
                    color={popupMessageColor}
                    text={popupMesage}
                    showMessage={showPopupMessage}
                  />
                  <div>
                    <div className={styles.inputLabel}>
                      New Email <span className={styles.required}>*</span>
                    </div>
                    <input
                      className={clsx(styles.inputField, errors?.email?.message && styles.error)}
                      type='text'
                      defaultValue={props.modalExistingEmail}
                      {...register('email', {
                        required: true,
                        validate: emailValidation,
                      })}
                    />
                    {errors?.email && (
                      <div>
                        <p className={styles.error}>{errors.email?.message}</p>
                      </div>
                    )}
                  </div>
                  <div className={styles.inputLabel}>
                    Password <span className={styles.required}>*</span>
                  </div>
                  <input
                    className={clsx(styles.inputField, errors.existingPassword && styles.error)}
                    type='password'
                    {...register('existingPassword', {
                      required: true,
                    })}
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className={styles.header}>
                  <label className={styles.title}>Change Password</label>
                  <button className={styles.closeModalButton} onClick={props.modalClose}>
                    ×
                  </button>
                </div>
                <div className={styles.form}>
                  <SHRMMessage
                    color={popupMessageColor}
                    text={popupMesage}
                    showMessage={showPopupMessage}
                  />
                  <div className={styles.inputLabel}>
                    Old Password <span className={styles.required}>*</span>
                  </div>
                  <input
                    className={clsx(
                      styles.inputField,
                      errors.existingPassword && styles.errorField
                    )}
                    type='password'
                    {...register('existingPassword', {
                      required: true,
                    })}
                  />
                  <div className={styles.inputLabel}>
                    New Password <span className={styles.required}>*</span>
                  </div>
                  <input
                    className={clsx(styles.inputField, errors.newPassword && styles.errorField)}
                    type='password'
                    onKeyDown={changeNewPassword}
                    {...register('newPassword', {
                      required: true,
                      validate: value => validatePassword(value),
                    })}
                  />
                  <div>
                    <p className={styles.error}>
                      {errors.newPassword && errors.newPassword.message}
                    </p>
                  </div>
                  <div className={styles.inputLabel}>
                    Confirm Password <span className={styles.required}>*</span>
                  </div>
                  <input
                    className={clsx(styles.inputField, errors.newConfirmation && styles.errorField)}
                    type='password'
                    {...register('newConfirmation', {
                      required: true,
                      validate: value =>
                        value === newPassword.current || 'The passwords do not match',
                    })}
                  />
                  <div>
                    <p className={styles.error}>
                      {errors.newConfirmation && errors.newConfirmation.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className={styles.footer}>
              <button type='submit' className={styles.saveButton} disabled={!(isDirty && isValid)}>
                Save
              </button>
              <div className={styles.cancelButton} onClick={props.modalClose}>
                Cancel
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AuthenticationModal;
